<script setup lang="ts">
import { computed } from 'vue'

import { useInsightActionCommands } from '../composables/useInsightActionCommands'
import { useInsightActionSelection } from '../composables/useInsightActionSelection'
import { useReasonText } from '../composables/useReasonText'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'

const { t } = useI18n()
const store = useInsightActionStore()
const selection = useInsightActionSelection()
const commands = useInsightActionCommands()
const { requestError } = useReasonText()

const group = computed(() => selection.group.value)

const platforms = computed(() =>
  selection.channels.value
    .filter((channel) => channel.actionable > 0)
    .map((channel) => channel.key.replaceAll('_', ' '))
    .join(', '),
)

const byCampaign = computed(() => {
  const tally = new Map<string, number>()
  for (const row of selection.listed.value) {
    if (!store.selection.has(row.rowKey) || !row.campaignLabel) continue
    tally.set(row.campaignLabel, (tally.get(row.campaignLabel) ?? 0) + 1)
  }
  return [...tally.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
})

const unlabeled = computed(() =>
  selection.listed.value.filter((row) => store.selection.has(row.rowKey) && !row.campaignLabel),
)
const ROW_SUMMARY_LIMIT = 10

const headline = computed(() =>
  t('insightActions.confirm.headline', {
    count: store.selectedCount,
    platforms: platforms.value,
    where: byCampaign.value.length
      ? t('insightActions.confirm.whereCampaigns', { count: byCampaign.value.length })
      : '',
  }),
)

const isStale = computed(() => store.applyErrorCode === 'INSIGHT_DATA_STALE')
const applyError = computed(() => requestError(store.applyErrorCode))
</script>

<template>
  <div v-if="group" class="insight-action-confirm grid gap-md min-w-0">
    <p class="m-0 text-base font-semibold leading-snug">{{ headline }}</p>
    <p class="m-0 text-sm text-muted">
      {{ t('insightActions.confirm.realChange', { platforms }) }}
    </p>

    <div v-if="byCampaign.length" class="overflow-hidden rounded-md border border-subtle">
      <div
        v-for="entry in byCampaign"
        :key="entry.name"
        class="flex items-center justify-between gap-sm border-b border-subtle px-sm py-xs text-sm last:border-0"
      >
        <span class="min-w-0 truncate" :title="entry.name">{{ entry.name }}</span>
        <span class="font-semibold">{{ entry.count }}</span>
      </div>
    </div>

    <div
      v-if="unlabeled.length"
      class="overflow-hidden rounded-md border border-subtle"
      data-testid="confirm-row-summaries"
    >
      <div
        v-for="row in unlabeled.slice(0, ROW_SUMMARY_LIMIT)"
        :key="row.rowKey"
        class="border-b border-subtle px-sm py-xs text-sm last:border-0"
      >
        <span class="block truncate" :title="row.summary || row.rowKey">{{
          row.summary || row.rowKey
        }}</span>
      </div>
      <div v-if="unlabeled.length > ROW_SUMMARY_LIMIT" class="px-sm py-xs text-xs text-muted">
        {{ t('insightActions.confirm.more', { count: unlabeled.length - ROW_SUMMARY_LIMIT }) }}
      </div>
    </div>

    <Alert
      v-if="group.approval?.approvalRequired"
      tone="info"
      :title="t('insightActions.confirm.approval', { role: group.approval.requiredRole })"
      data-testid="confirm-approval"
    />

    <p class="m-0 text-xs text-muted">
      {{
        group.revertible
          ? t('insightActions.confirm.revertible')
          : t('insightActions.confirm.notRevertible')
      }}
    </p>

    <Alert
      v-if="isStale"
      tone="warning"
      :title="t('insightActions.confirm.staleNotice')"
      data-testid="stale-notice"
    >
      <Button
        variant="outline"
        size="xs"
        data-testid="stale-refresh"
        @click="commands.recoverFromStale()"
      >
        {{ t('insightActions.confirm.staleRefresh') }}
      </Button>
    </Alert>

    <Alert
      v-else-if="store.applyErrorCode"
      tone="warning"
      :title="applyError.message"
      data-testid="apply-error"
    >
      <span class="font-mono text-2xs">{{ applyError.code }}</span>
    </Alert>
  </div>
</template>
